/*
 * Khonology design language — derived from khonology.com
 *
 *   Brand red      #C42727  (headline / accent red)
 *   Soft red       #FD6262  (hover & dark-mode accent)
 *   Ink            #141416  (near-black surfaces & type)
 *   Paper          #FAFAF8  (warm white canvas)
 *
 * Type: Poppins (display — semibold, uppercase, letterspaced, as used for
 * "UNLOCKING VALUE USING OUR DIGITAL SOLUTIONS") over Barlow (UI/body).
 * Red is used sparingly and sharply: the header rule, the sidebar
 * indicator, primary actions and links — never as a flood fill.
 */
import {
  createUnifiedTheme,
  genPageTheme,
  palettes,
  shapes,
} from '@backstage/theme';

const BRAND_RED = '#C42727';
const SOFT_RED = '#FD6262';
const INK = '#141416';
const INK_RAISED = '#1F1F22';
const PAPER = '#FAFAF8';

const DISPLAY_FONT = '"Poppins", "Helvetica Neue", Arial, sans-serif';
const BODY_FONT = '"Barlow", "Helvetica Neue", Arial, sans-serif';

const heading = (fontSize: number, fontWeight = 600) => ({
  fontFamily: DISPLAY_FONT,
  fontSize,
  fontWeight,
  marginBottom: 10,
});

const typography = {
  htmlFontSize: 16,
  fontFamily: BODY_FONT,
  h1: heading(38),
  h2: heading(30),
  h3: heading(24),
  h4: heading(20),
  h5: heading(17),
  h6: heading(15),
};

/** Flat ink page header; the wave is ink-on-ink so it reads as texture. */
const inkPage = genPageTheme({
  colors: [INK, INK_RAISED],
  shape: shapes.wave,
});

const navigation = (indicator: string) => ({
  background: INK,
  indicator,
  color: '#B9B9BC',
  selectedColor: '#FFFFFF',
  navItem: { hoverBackground: INK_RAISED },
  submenu: { background: INK_RAISED },
});

const sharedComponents = (accent: string) => ({
  BackstageHeader: {
    styleOverrides: {
      header: {
        backgroundImage: 'none',
        backgroundColor: INK,
        borderBottom: `4px solid ${accent}`,
        boxShadow: 'none',
      },
      title: {
        fontFamily: DISPLAY_FONT,
        fontWeight: 600,
        textTransform: 'uppercase' as const,
        letterSpacing: '0.06em',
      },
      subtitle: { color: 'rgba(255,255,255,0.66)' },
    },
  },
  BackstageHeaderLabel: {
    styleOverrides: {
      label: { color: 'rgba(255,255,255,0.92)' },
      value: { color: 'rgba(255,255,255,0.66)' },
    },
  },
  BackstageItemCardHeader: {
    styleOverrides: {
      root: {
        backgroundImage: 'none',
        backgroundColor: INK,
        borderBottom: `3px solid ${accent}`,
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 2,
        fontFamily: DISPLAY_FONT,
        fontWeight: 600,
        textTransform: 'uppercase' as const,
        letterSpacing: '0.08em',
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        fontFamily: DISPLAY_FONT,
        fontWeight: 500,
        textTransform: 'uppercase' as const,
        letterSpacing: '0.06em',
      },
    },
  },
});

export const khonologyLightTheme = createUnifiedTheme({
  palette: {
    ...palettes.light,
    primary: { main: BRAND_RED },
    secondary: { main: INK },
    background: { default: PAPER, paper: '#FFFFFF' },
    banner: {
      ...palettes.light.banner,
      info: INK,
      link: BRAND_RED,
    },
    navigation: navigation(BRAND_RED),
    link: BRAND_RED,
    linkHover: SOFT_RED,
  },
  fontFamily: BODY_FONT,
  typography,
  defaultPageTheme: 'home',
  pageTheme: { home: inkPage },
  components: sharedComponents(BRAND_RED),
});

export const khonologyDarkTheme = createUnifiedTheme({
  palette: {
    ...palettes.dark,
    primary: { main: SOFT_RED },
    secondary: { main: '#FFFFFF' },
    background: { default: '#0E0E10', paper: '#17171A' },
    navigation: navigation(SOFT_RED),
    link: SOFT_RED,
    linkHover: '#FF8A8A',
  },
  fontFamily: BODY_FONT,
  typography,
  defaultPageTheme: 'home',
  pageTheme: { home: inkPage },
  components: sharedComponents(SOFT_RED),
});
